import { CustomProvider } from 'firebase/app-check';

/**
 * App Check through player-backend: reCAPTCHA Enterprise runs in the browser, player-backend
 * assesses the token (validity, action, hostname, bot score) and mints the App Check token.
 * The backend owns the score policy, so a bot can be refused without a client release.
 *
 * player-backend's `app_check_backend_enabled` flag is the switch: when it is off the route answers
 * `503 {"error":"disabled"}` and this provider does what Firebase's ReCaptchaEnterpriseProvider does,
 * the reCAPTCHA exchange at Google (action `fire_app_check`).
 */

/** Must match the backend's expected action. */
export const APP_CHECK_RECAPTCHA_ACTION = 'app_check';
/** Firebase's own action for its exchange (ReCaptchaEnterpriseProvider), kept for the Google fallback. */
export const FIREBASE_RECAPTCHA_ACTION = 'fire_app_check';
/** How long a `disabled` answer sends every token straight to Google, so a page asks the backend once. */
export const BACKEND_DISABLED_MEMO_MS = 5 * 60 * 1000;

const GOOGLE_EXCHANGE_BASE_URL = 'https://content-firebaseappcheck.googleapis.com/v1';

const GRECAPTCHA_WAIT_MS = 10_000;
const GRECAPTCHA_POLL_MS = 100;
const EXCHANGE_TIMEOUT_MS = 10_000;
/** Below this the SDK would refresh in a tight loop; the backend mints for an hour. */
const MIN_TTL_MILLIS = 5 * 60 * 1000;

interface GrecaptchaEnterprise {
  ready(callback: () => void): void;
  render(container: HTMLElement, params: { sitekey: string; size: 'invisible' }): number;
  /** A key loaded through an explicit `render` widget must be executed by its widget id, not its site key. */
  execute(widgetId: number, options: { action: string }): Promise<string>;
}

export type AppCheckExchangeSource = 'backend' | 'google';

/** A failed exchange. `code` is a closed error value (the backend's, or Google's status enum), never free text. */
export class AppCheckExchangeError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly source: AppCheckExchangeSource = 'backend',
  ) {
    super(`App Check exchange failed (${source} ${status} ${code})`);
    this.name = 'AppCheckExchangeError';
  }
}

/** player-backend's answer while `app_check_backend_enabled` is off. Anything else is a verdict, never a fallback. */
export const isBackendDisabled = (e: unknown): boolean =>
  e instanceof AppCheckExchangeError && e.source === 'backend' && e.status === 503 && e.code === 'disabled';

const grecaptchaEnterprise = () =>
  (window as unknown as { grecaptcha?: { enterprise?: GrecaptchaEnterprise } }).grecaptcha?.enterprise;

/** enterprise.js is a deferred script in html/index.html, so it may not exist yet at boot. */
const waitForGrecaptcha = (): Promise<GrecaptchaEnterprise> =>
  new Promise((resolve, reject) => {
    // Counted polls, not a Date.now() deadline: a clock that jumps (a faked or a corrected one) can neither
    // end the wait early nor keep it polling far past GRECAPTCHA_WAIT_MS.
    let pollsLeft = GRECAPTCHA_WAIT_MS / GRECAPTCHA_POLL_MS;
    const poll = () => {
      const enterprise = grecaptchaEnterprise();
      if (enterprise) {
        enterprise.ready(() => resolve(enterprise));
      } else if (pollsLeft-- <= 0) {
        reject(new Error('reCAPTCHA Enterprise did not load'));
      } else {
        setTimeout(poll, GRECAPTCHA_POLL_MS);
      }
    };
    poll();
  });

/**
 * enterprise.js is loaded with the Auth key (`render=`), so the App Check key gets its own invisible
 * widget, once. `execute(siteKey)` rejects for such a key ("not loaded in api.js"): execute the widget id.
 */
const widgetIdByKey = new Map<string, number>();
const widgetIdFor = (enterprise: GrecaptchaEnterprise, siteKey: string): number => {
  const existing = widgetIdByKey.get(siteKey);
  if (existing !== undefined) return existing;
  const container = document.createElement('div');
  container.style.display = 'none';
  document.body.appendChild(container);
  const widgetId = enterprise.render(container, { sitekey: siteKey, size: 'invisible' });
  widgetIdByKey.set(siteKey, widgetId);
  return widgetId;
};

export const executeRecaptcha = async (siteKey: string, action = APP_CHECK_RECAPTCHA_ACTION): Promise<string> => {
  const enterprise = await waitForGrecaptcha();
  return enterprise.execute(widgetIdFor(enterprise, siteKey), { action });
};

export interface AppCheckTokenResult {
  token: string;
  expireTimeMillis: number;
}

const readField = (body: unknown, name: string): unknown =>
  body && typeof body === 'object' ? (body as Record<string, unknown>)[name] : undefined;

export const exchangeRecaptchaToken = async (apiUrl: string, recaptchaToken: string): Promise<AppCheckTokenResult> => {
  const response = await fetch(`${apiUrl}/v1/app-check/recaptcha`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ platform: 'web', recaptcha_token: recaptchaToken }),
    // Anonymous by design: this runs before sign-in and attests WHAT is calling, not who.
    credentials: 'omit',
    signal: AbortSignal.timeout(EXCHANGE_TIMEOUT_MS),
  });

  const body: unknown = await response.json().catch(() => null);
  const field = (name: string) => readField(body, name);

  if (!response.ok) {
    const code = field('error');
    throw new AppCheckExchangeError(response.status, typeof code === 'string' ? code : 'unknown');
  }

  const token = field('token');
  const ttlMillis = field('ttl_millis');
  if (typeof token !== 'string' || token === '') throw new AppCheckExchangeError(response.status, 'malformed_token');
  if (typeof ttlMillis !== 'number' || !Number.isFinite(ttlMillis) || ttlMillis < MIN_TTL_MILLIS) {
    throw new AppCheckExchangeError(response.status, 'malformed_ttl');
  }

  // A duration, not the server's expires_at: keeps the SDK's refresh arithmetic on one clock.
  return { token, expireTimeMillis: Date.now() + ttlMillis };
};

/** The fields of the app's Firebase config that address its App Check exchange. */
export interface FirebaseAppIdentity {
  projectId?: string;
  appId?: string;
  apiKey?: string;
}

/** Google's duration format, e.g. "3600s" (fractions allowed). */
const GOOGLE_TTL_PATTERN = /^(\d+(?:\.\d+)?)s$/;
/** Google's error `status` is a closed enum (PERMISSION_DENIED, ...); anything else is reported as unknown. */
const GOOGLE_STATUS_PATTERN = /^[A-Z_]{1,64}$/;

/** What Firebase's ReCaptchaEnterpriseProvider does: Google assesses the reCAPTCHA token and mints. */
export const exchangeRecaptchaTokenWithGoogle = async (
  app: FirebaseAppIdentity,
  recaptchaToken: string,
): Promise<AppCheckTokenResult> => {
  const { projectId, appId, apiKey } = app;
  if (!projectId || !appId || !apiKey) throw new AppCheckExchangeError(0, 'missing_firebase_config', 'google');

  const response = await fetch(
    `${GOOGLE_EXCHANGE_BASE_URL}/projects/${projectId}/apps/${appId}:exchangeRecaptchaEnterpriseToken?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ recaptcha_enterprise_token: recaptchaToken }),
      credentials: 'omit',
      signal: AbortSignal.timeout(EXCHANGE_TIMEOUT_MS),
    },
  );

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const status = readField(readField(body, 'error'), 'status');
    const code = typeof status === 'string' && GOOGLE_STATUS_PATTERN.test(status) ? status : 'unknown';
    throw new AppCheckExchangeError(response.status, code, 'google');
  }

  const token = readField(body, 'token');
  const ttl = readField(body, 'ttl');
  if (typeof token !== 'string' || token === '') {
    throw new AppCheckExchangeError(response.status, 'malformed_token', 'google');
  }
  const ttlSeconds = typeof ttl === 'string' ? GOOGLE_TTL_PATTERN.exec(ttl)?.[1] : undefined;
  const ttlMillis = ttlSeconds === undefined ? NaN : Number(ttlSeconds) * 1000;
  if (!Number.isFinite(ttlMillis) || ttlMillis < MIN_TTL_MILLIS) {
    throw new AppCheckExchangeError(response.status, 'malformed_ttl', 'google');
  }

  return { token, expireTimeMillis: Date.now() + ttlMillis };
};

export interface BackendAppCheckProviderOptions {
  apiUrl: string;
  siteKey: string;
  firebase: FirebaseAppIdentity;
}

/**
 * The provider's getToken, plus which exchange the last call went to (for error reporting).
 * A `disabled` answer is remembered in memory for BACKEND_DISABLED_MEMO_MS; any other failure throws as is.
 */
export const createAppCheckTokenSource = ({ apiUrl, siteKey, firebase }: BackendAppCheckProviderOptions) => {
  let backendDisabledUntil = 0;
  let lastPath: AppCheckExchangeSource | undefined;

  const viaGoogle = async () => {
    lastPath = 'google';
    return exchangeRecaptchaTokenWithGoogle(firebase, await executeRecaptcha(siteKey, FIREBASE_RECAPTCHA_ACTION));
  };

  const getToken = async (): Promise<AppCheckTokenResult> => {
    if (Date.now() < backendDisabledUntil) return viaGoogle();
    lastPath = 'backend';
    try {
      return await exchangeRecaptchaToken(apiUrl, await executeRecaptcha(siteKey));
    } catch (e) {
      if (!isBackendDisabled(e)) throw e;
      backendDisabledUntil = Date.now() + BACKEND_DISABLED_MEMO_MS;
      return viaGoogle();
    }
  };

  return { getToken, lastPath: () => lastPath };
};

export const createBackendAppCheckProvider = (options: BackendAppCheckProviderOptions) => {
  const source = createAppCheckTokenSource(options);
  return { provider: new CustomProvider({ getToken: source.getToken }), lastPath: source.lastPath };
};
